import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { Search, ChevronDown, Smile } from "lucide-react";
import {
  getAllPolls,
  getAllPosts,
  addVote,
  addRection,
  getAll,
  getAllGroups,
} from "../../ApiHelper/url_Helpers";
import { toasterWarning } from "../../Components/toaster";
import Loader from "../../Components/Loader";
import { BiLeftArrow, BiRightArrow } from "react-icons/bi";
import SortDropdown from "../../Components/SortDropDown";
import { sortEngage } from "../Dashboard/DashboardData";
import { useLocation, useNavigate } from "react-router-dom";
import FilterDropdown from "../Approvals/ApprovalFilter";

/* ---------------- Reactions ---------------- */
const reactions = [
  { name: "Like", emoji: "👍" },
  { name: "Love", emoji: "❤️" },
  { name: "Clap", emoji: "👏" },
  { name: "Celebrate", emoji: "🎉" },
  { name: "Thinking", emoji: "🤔" },
];

const ENGAGE_SEARCH_KEY = "engage_search_query";

export const useDebounce = (value, delay = 500) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debouncedValue;
};

const parseUrlParams = (search) => {
  const params = new URLSearchParams(search);
  return {
    page: Number(params.get("page")) || 1,
    type: params.get("type") || "all",
    ordering: params.get("ordering") || "newest",
  };
};

const buildSearch = (current, overrides = {}) => {
  const next = { ...current, ...overrides };
  const params = new URLSearchParams();
  if (next.page > 1) params.set("page", next.page);
  if (next.type !== "all") params.set("type", next.type);
  if (next.ordering !== "newest") params.set("ordering", next.ordering);
  return params.toString();
};


export const Engage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = useMemo(
    () => parseUrlParams(location.search),
    [location.search],
  );
  const { page, type, ordering } = urlParams;

  const [posts, setPosts] = useState([]);
  const [polls, setPolls] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  const [inputSearch, setInputSearch] = useState(() => {
    return localStorage.getItem(ENGAGE_SEARCH_KEY) || "";
  });
  const [groupFilter, setGroupFilter] = useState("all");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [openEmojiPost, setOpenEmojiPost] = useState(null);

  const debouncedSearch = useDebounce(inputSearch, 500);

  const votedPollsRef = useRef(new Set());
  const groupsFetchedRef = useRef(false);
  const dropdownRef = useRef(null);
  const groupDropdownRef = useRef(null);
  const emojiRef = useRef(null);

  const pageSize = 10;

// Groups list 
  useEffect(() => {
    if (groupsFetchedRef.current) return;
    groupsFetchedRef.current = true;
    getAllGroups().then((res) => setGroups(res?.results || []));
  }, []);

  useEffect(() => {
    localStorage.setItem(ENGAGE_SEARCH_KEY, debouncedSearch);
  }, [debouncedSearch]);


  const fetchFeed = useCallback(async (params, group, searchQuery) => {
    const { type, ordering } = params;
    const apiParams = {};
    if (searchQuery) apiParams.search = searchQuery;
    if (group !== "all") apiParams.group_name = group;
    if (ordering !== "newest") apiParams.ordering = ordering;

    setLoading(true);
    try {
      if (type === "posts") {
        const res = await getAllPosts(apiParams);
        setPosts(res?.results ?? res ?? []);
        setPolls([]);
      } else if (type === "polls") {
        const res = await getAllPolls(apiParams);
        setPolls(res?.results ?? res ?? []);
        setPosts([]);
      } else {
        const res = await getAll(apiParams);
        setPosts(res?.posts ?? []);
        setPolls(res?.polls ?? []);
      }
    } catch (err) {
      console.error("Engage fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFeed(urlParams, groupFilter, debouncedSearch);
  }, [location.search, groupFilter, debouncedSearch]);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setShowFilterDropdown(false);
      if (
        groupDropdownRef.current &&
        !groupDropdownRef.current.contains(e.target)
      )
        setShowGroupDropdown(false);
      if (emojiRef.current && !emojiRef.current.contains(e.target))
        setOpenEmojiPost(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);


  const handleReaction = async (postId, reactionType) => {
    try {
      await addRection(postId, { reaction_type: reactionType });
      setOpenEmojiPost(null);
      fetchFeed(urlParams, groupFilter);
    } catch (error) {
      console.error("Add reaction failed:", error);
    }
  };

  const handleVote = async (pollId, optionId) => {
    if (votedPollsRef.current.has(pollId)) return;
    votedPollsRef.current.add(pollId);
    try {
      await addVote(pollId, { option_id: optionId });
      fetchFeed(urlParams, groupFilter);
    } catch (err) {
      votedPollsRef.current.delete(pollId);
      console.error("Vote failed:", err);
    }
  };

  const pushUrl = (overrides) => {
    navigate({ search: buildSearch(urlParams, overrides) }, { replace: true });
  };

  const resetAll = () => {
    setInputSearch("");
    localStorage.removeItem(ENGAGE_SEARCH_KEY);
    setGroupFilter("all");
    navigate(location.pathname, { replace: true });
  };

  const mixedFeed = useMemo(() => {
    const postItems = (posts?.results ?? posts ?? []).map((p) => ({
      ...p,
      _type: "post",
      _date: p.created_at,
    }));
    const pollItems = (polls?.results ?? polls ?? []).map((p) => ({
      ...p,
      _type: "poll",
      _date: p.created_at || p.start_at || p.end_at,
    }));

    let all = [];
    if (type === "posts") all = postItems;
    else if (type === "polls") all = pollItems;
    else all = [...postItems, ...pollItems];

    return all.sort((a, b) =>
      ordering === "oldest"
        ? new Date(a._date) - new Date(b._date)
        : new Date(b._date) - new Date(a._date),
    );
  }, [posts, polls, type, ordering]);

  const pagedFeed = useMemo(() => {
    const start = (page - 1) * pageSize;
    return mixedFeed.slice(start, start + pageSize);
  }, [mixedFeed, page]);

  const totalItems = mixedFeed.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const startItem = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalItems);

  const formatDate = (date) =>
    new Date(date).toLocaleDateString("en-US", {
      month: "long",
      day: "2-digit",
      year: "numeric",
    });

  const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";

  const reactionEmojiMap = reactions.reduce((acc, r) => {
    acc[r.name.toLowerCase()] = r.emoji;
    return acc;
  }, {});

  const getAllUniqueReactions = (rxns = []) => [
    ...new Set(rxns.map((r) => capitalize(r.reaction))),
  ];

  const getFirstReactorLabel = (rxns = []) => {
    if (!rxns.length) return "";
    const firstName = rxns[0]?.employee_name?.split(" ")[0];
    return rxns.length === 1 ? firstName : `${firstName} & others`;
  };

  const groupsList = groups.map((item) => ({
    label: item?.name,
    key: item?.name,
  }));

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <h1 className="text-2xl font-bold mb-1">Engage</h1>
      <p className="text-gray-500 mb-6">
        Stay connected with your organization — participate in polls and react
        to posts.
      </p>

      <div className="flex flex-col sm:flex-row gap-3 justify-between">
        <div className="flex justify-between me-3">
         <div className="relative flex-grow sm:max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              value={inputSearch}
              onChange={(e) => setInputSearch(e.target.value)}
              placeholder="Search ('Quarterly Results'/'Office Party')"
              className="w-64 pl-10 pr-4 py-2 border rounded-lg text-sm"
            />
          </div>
        </div>

        <div className="flex gap-2">
          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setShowFilterDropdown((p) => !p)}
              className="px-4 py-2 bg-white border rounded-lg text-sm flex items-center justify-between gap-2 min-w-[130px]"
            >
              <span>
                {type === "all"
                  ? "All Activities"
                  : type === "posts"
                    ? "Posts"
                    : "Polls"}
              </span>
              <ChevronDown size={14} />
            </button>

            {showFilterDropdown && (
              <div className="absolute mt-2 bg-white border rounded-lg shadow-md w-full z-10">
                {[
                  { value: "all", label: "All Activities" },
                  { value: "posts", label: "Posts" },
                  { value: "polls", label: "Polls" },
                ].map(({ value, label }) => (
                  <p
                    key={value}
                    onClick={() => {
                      pushUrl({ type: value, page: 1 });
                      setShowFilterDropdown(false);
                    }}
                    className={`px-4 py-2 text-xs cursor-pointer hover:bg-gray-100 ${type === value ? "font-semibold text-cyan-600" : ""}`}
                  >
                    {label}
                  </p>
                ))}
              </div>
            )}
          </div>

          <div ref={groupDropdownRef} className="relative">
            <button
              onClick={() => setShowGroupDropdown((p) => !p)}
              className="px-4 py-2 bg-white border rounded-lg text-sm flex items-center justify-between gap-2 min-w-[150px]"
            >
              {groupFilter === "all" ? "All Groups" : groupFilter}
              <ChevronDown size={14} />
            </button>

            {showGroupDropdown && (
              <div className="absolute mt-2 bg-white border rounded-lg shadow-md w-40 z-10 max-h-64 overflow-y-auto min-w-[180px]">
                <p
                  onClick={() => {
                    setGroupFilter("all");
                    setShowGroupDropdown(false);
                  }}
                  className={`px-4 py-2 text-xs cursor-pointer hover:bg-gray-100 ${groupFilter === "all" ? "font-semibold text-cyan-600" : ""}`}
                >
                  All Groups
                </p>
                {groupsList.map((g) => (
                  <p
                    key={g.key}
                    onClick={() => {
                      setGroupFilter(g.key);
                      setShowGroupDropdown(false);
                    }}
                    className={`px-4 py-2 text-xs cursor-pointer hover:bg-gray-100 ${groupFilter === g.key ? "font-semibold text-cyan-600" : ""}`}
                  >
                    {g.label}
                  </p>
                ))}
              </div>
            )}
          </div>

          <div className="relative me-3">
            <FilterDropdown
              list={sortEngage}
              title="Sort By"
              keyName="ordering"
              name="Sort By"
              value={{ ordering }}
              onChange={(next) => {
                pushUrl({ ordering: next.ordering || "newest", page: 1 });
                setShowFilterDropdown(false);
                setShowGroupDropdown(false);
              }}
            />
          </div>

          <button
            type="button"
            onClick={resetAll}
            className="flex-1 sm:flex-none border border-gray-300 rounded-md py-2 px-4 text-sm font-semibold text-gray-500 hover:bg-gray-100"
          >
            Reset All
          </button>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <div className="max-w-4xl mx-auto mt-8 space-y-6">
          {pagedFeed.length === 0 && (
            <div className="text-center text-gray-400 py-16 text-sm">
              No results found.
            </div>
          )}

          {pagedFeed.map((item) => {
            if (item._type === "post") {
              return (
                <div
                  key={`post-${item.id}`}
                  className="relative bg-white rounded-2xl p-5 border shadow-sm hover:shadow-md transition"
                >
                  {/* Header */}
                  <div className="flex justify-between items-center mb-3">
                    <div className="flex items-center gap-2">
                      <img
                        src="/icons/EngageAvtar.png"
                        className="w-9 h-9 rounded-full"
                        alt="Group Post"
                      />
                      <div>
                        <p className="text-sm font-semibold">
                          {item.group_name}
                        </p>
                        <p className="text-[11px] text-gray-400">
                          {formatDate(item.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Image */}
                  <div className="border-t pt-4 flex justify-center">
                    <div className="relative w-full max-w-[520px] h-[300px] rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center">
                      <img
                        src={item.attachment_url || "/icons/EngageAvtar.png"}
                        alt="post"
                        className="max-w-full max-h-full object-contain"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent pointer-events-none" />
                      <div className="absolute bottom-3 left-3 right-3">
                        <h3 className="text-white text-base md:text-lg font-bold leading-snug">
                          {item.title}
                        </h3>
                      </div>
                    </div>
                  </div>

                  <p className="mt-3 text-sm text-gray-500">
                    {item.description}
                  </p>

                  {/* Actions */}
                  <div className="mt-4 flex items-center justify-between relative">
                    <div
                      className="relative"
                      onMouseEnter={() => setOpenEmojiPost(item.id)}
                    >
                      <button
                        onClick={() =>
                          setOpenEmojiPost(
                            openEmojiPost === item.id ? null : item.id,
                          )
                        }
                        className="flex items-center gap-2 text-gray-500 text-sm hover:text-gray-700"
                      >
                        <Smile size={18} />
                        React
                      </button>

                      {openEmojiPost === item.id && (
                        <div
                          ref={emojiRef}
                          className="absolute bottom-full mb-2 left-0 bg-white border rounded-xl shadow-lg p-2 flex gap-2 z-20"
                        >
                          {reactions.map((r) => (
                            <button
                              key={r.name}
                              className="text-lg hover:scale-110 transition"
                              onClick={() => handleReaction(item.id, r.name)}
                            >
                              {r.emoji}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Reactions Summary */}
                    <div className="relative group inline-flex items-center gap-2">
                      <div className="flex -space-x-1 cursor-pointer">
                        {getAllUniqueReactions(item.reactions).map(
                          (reaction, i) => (
                            <span
                              key={i}
                              className="w-6 h-6 flex items-center justify-center bg-white rounded-full shadow text-sm"
                              title={reaction}
                            >
                              {reactionEmojiMap[reaction?.toLowerCase()]}
                            </span>
                          ),
                        )}
                      </div>
                      {item.reactions?.length > 0 && (
                        <span className="text-sm text-gray-600">
                          {getFirstReactorLabel(item.reactions)}
                        </span>
                      )}
                      <span className="text-sm text-gray-500 font-medium cursor-pointer">
                        {item.reactions_count || item.reactions?.length || 0}
                      </span>
                      {item.reactions?.length > 0 && (
                        <div className="absolute bottom-full mb-2 left-0 w-64 bg-white text-gray-800 text-xs rounded-xl shadow-xl border opacity-0 group-hover:opacity-100 transition-all duration-200 z-30">
                          <div className="p-2 max-h-40 overflow-y-auto">
                            {item.reactions.map((r, i) => (
                              <div
                                key={i}
                                className="flex items-center gap-2 py-1 px-1 rounded hover:bg-gray-50"
                              >
                                <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-[11px] font-bold">
                                  {r.employee_name?.charAt(0).toUpperCase()}
                                </div>
                                <span className="flex-1 text-[12px] font-medium truncate">
                                  {r.employee_name}
                                </span>
                                <span className="text-base flex items-center gap-1">
                                  {reactionEmojiMap[r.reaction?.toLowerCase()]}
                                  <span className="text-[11px] text-gray-400">
                                    {capitalize(r.reaction)}
                                  </span>
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            }

            /* ── POLL card ── */
            if (item._type === "poll") {
              return (
                <div
                  key={`poll-${item.id}`}
                  className="bg-white rounded-2xl p-6 border shadow-sm hover:shadow-md transition"
                >
                  <h3 className="font-bold mb-1">{item.description}</h3>
                  <p className="text-sm text-gray-600 mb-5">{item.question}</p>

                  <div className="space-y-3">
                    {item?.options.map((opt) => {
                      const hasSelectedOption = item?.options?.some(
                        (op) => op?.is_selected,
                      );
                      const isSelected = opt.is_selected;
                      return (
                        <div
                          key={opt.id}
                          onClick={() => {
                            if (item.is_active && !hasSelectedOption) {
                              handleVote(item.id, opt.id);
                            }
                            if (hasSelectedOption) {
                              toasterWarning(
                                "You've already voted on this poll",
                              );
                            }
                          }}
                          className={`border rounded-lg p-3 flex items-center justify-between transition
                            ${isSelected ? "bg-green-50 border-green-400" : "hover:bg-gray-50 cursor-pointer"}
                            ${!item.is_active ? "opacity-60 cursor-not-allowed" : ""}`}
                        >
                          <span className="text-sm font-medium">
                            {opt.text}
                          </span>
                          {isSelected && (
                            <span className="text-[11px] px-2 py-0.5 bg-green-100 text-green-600 rounded-full">
                              Your Vote
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="pt-4 flex justify-between text-xs text-gray-500">
                    <span>
                      Votes: <strong>{item.total_votes}</strong>
                    </span>
                    <span>
                      Ends: <strong>{formatDate(item.end_at)}</strong>
                    </span>
                  </div>
                </div>
              );
            }

            return null;
          })}
        </div>
      )}
      
      <div className="flex items-center justify-between px-4 py-3 mt-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">
            Displaying{" "}
            <span className="font-semibold text-gray-800">
              {startItem}-{endItem}
            </span>{" "}
            of <span className="font-semibold text-gray-800">{totalItems}</span>
          </span>
        </div>

        <div className="flex items-center justify-center gap-3">
          <button
            disabled={page === 1}
            onClick={() => pushUrl({ page: page - 1 })}
            className="flex items-center gap-2 px-4 py-2 min-w-[90px] justify-center rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <BiLeftArrow />
            <span>Prev</span>
          </button>
          <span className="min-w-[24px] text-center text-sm font-semibold text-gray-700">
            {page}
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => pushUrl({ page: page + 1 })}
            className="flex items-center gap-2 px-4 py-2 min-w-[90px] justify-center rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span>Next</span>
            <BiRightArrow />
          </button>
        </div>
      </div>
    </div>
  );
};
